import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchCompanyByName, createNewCompany } from "../../apis/CompanyApis";
import { createNewJob } from "../../apis/JobApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import SeoMeta from "../SeoMeta";
import JobPreview from "./JobPreview";
import { JobCard } from "../jobs/JobCard";

const DEFAULT_LOGO =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTV7bGzGrD43rwLoeDHYEbzDydSI3qlupGR3g&s";

const MANDATORY_FIELDS = [
  "companyName",
  "title",
  "description",
  "type",
  "experience",
  "primarySkills",
  "applyUrl",
];

const SAMPLE_JSON = `{
  "companyName": "Google",
  "title": "Software Engineer",
  "description": ["Build scalable systems", "Work with cross-functional teams"],
  "type": "job",
  "experience": ["0-2 years", "Freshers"],
  "primarySkills": ["JavaScript", "React", "Node.js"],
  "secondarySkills": ["AWS", "Docker"],
  "locations": ["Bangalore", "Remote"],
  "applyUrl": "https://careers.google.com/jobs/123",
  "expiryDate": "2025-03-31T23:59:59Z"
}`;

export default function AddNewJob() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  const [jobJson, setJobJson] = useState("");
  const [parsedJob, setParsedJob] = useState(null);
  const [error, setError] = useState("");
  const [logoWarning, setLogoWarning] = useState("");
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Live JSON validation
  const validateJson = async (value) => {
    setError("");
    setLogoWarning("");
    setParsedJob(null);

    let json;
    try {
      json = JSON.parse(value);
    } catch {
      setError("Invalid JSON format. Please check your syntax.");
      return;
    }

    // Check mandatory fields
    const missingFields = MANDATORY_FIELDS.filter(
      (field) =>
        !json[field] ||
        (Array.isArray(json[field])
          ? json[field].length === 0
          : String(json[field]).trim() === "")
    );

    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Auto-fill createdAt and updatedAt if missing
    const now = new Date();
    if (!json.createdAt) json.createdAt = now.toISOString();
    if (!json.updatedAt) json.updatedAt = now.toISOString();

    setIsFetchingLogo(true);

    try {
      const companyData = await fetchCompanyByName(json.companyName);
      if (companyData?.logo) {
        json.companyLogo = companyData.logo;
        json.companyExists = true;
      } else {
        json.companyLogo = json.companyLogo || DEFAULT_LOGO;
        json.companyExists = false;
        setLogoWarning(
          "Company logo not found. Default logo will be used if none provided."
        );
      }
      setParsedJob(json);
      setJobJson(JSON.stringify(json, null, 2));
    } catch (err) {
      console.error(err);
      json.companyLogo = json.companyLogo || DEFAULT_LOGO;
      json.companyExists = false;
      setParsedJob(json);
      setJobJson(JSON.stringify(json, null, 2));
      setLogoWarning(
        "Company not found in database. A new company will be created."
      );
    } finally {
      setIsFetchingLogo(false);
    }
  };

  // Submit Job
  const handleCreateJob = async () => {
    if (!parsedJob) return;

    try {
      setIsSubmitting(true);
      let jobData = { ...parsedJob };

      // Step 1: Create company if it doesn't exist
      if (!jobData.companyExists) {
        const companyPayload = {
          name: jobData.companyName,
          logo: jobData.companyLogo || DEFAULT_LOGO,
        };
        const createdCompany = await createNewCompany(
          companyPayload,
          user.token
        );
        jobData.companyId = createdCompany._id;
        jobData.companyExists = true;
        jobData.companyLogo = companyPayload.logo;
      }

      // Step 2: Create job
      const createdJob = await createNewJob(jobData, user.token);
      if (createdJob) {
        successToast("Job created successfully!");
        setJobJson("");
        setParsedJob(null);
        setError("");
        setLogoWarning("");
      }
    } catch (err) {
      console.error(err);
      errorToast("Failed to create job. Please try again.");
      setError("Failed to create job. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadSample = () => {
    setJobJson(SAMPLE_JSON);
    setError("");
    setLogoWarning("");
    setParsedJob(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoMeta
        title="Add New Job | Admin - Code Arena"
        description="Add a new job posting to Code Arena. Validate JSON, create company if missing, and submit jobs easily."
        url="https://www.code-arena.in/admin/add/new"
        image="/logo192.png"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="ca-container py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <i className="ri-briefcase-line text-xl"></i>
                </div>
                <h1 className="text-2xl font-bold">Add New Job</h1>
              </div>
              <p className="text-blue-100">
                Create a new job posting by pasting valid JSON
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
              >
                <i className="ri-arrow-left-line"></i> Back to Admin
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="ca-container py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - JSON Input */}
          <div className="space-y-6">
            <div className="ca-card overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <i className="ri-code-s-slash-line text-blue-600"></i>
                  Job JSON
                </h2>
                <button
                  type="button"
                  onClick={loadSample}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  Load Sample
                </button>
              </div>

              <div className="p-6">
                <textarea
                  value={jobJson}
                  onChange={(e) => setJobJson(e.target.value)}
                  placeholder="Paste job JSON here..."
                  className="ca-input w-full h-80 font-mono text-sm resize-none"
                  spellCheck={false}
                />

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => validateJson(jobJson)}
                    disabled={isFetchingLogo || isSubmitting || !jobJson.trim()}
                    className="ca-btn-primary flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 disabled:opacity-50"
                  >
                    {isFetchingLogo ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Validating...
                      </>
                    ) : (
                      <>
                        <i className="ri-checkbox-circle-line"></i>
                        Validate JSON
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleCreateJob}
                    disabled={!parsedJob || !!error || isSubmitting}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
                      parsedJob && !error
                        ? "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="ri-add-circle-line"></i>
                        Create Job
                      </>
                    )}
                  </button>
                </div>

                {/* Messages */}
                {error && (
                  <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="font-medium text-red-800">
                          Validation Error
                        </p>
                        <p className="text-sm text-red-600 mt-1">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {logoWarning && (
                  <div className="mt-4 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                    <div className="flex items-start gap-3">
                      <i className="ri-alert-line text-yellow-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="font-medium text-yellow-800">Note</p>
                        <p className="text-sm text-yellow-600 mt-1">
                          {logoWarning}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {parsedJob && !error && (
                  <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-green-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="font-medium text-green-800">
                          JSON Valid!
                        </p>
                        <p className="text-sm text-green-600 mt-1">
                          Ready to create job. Check the preview on the right.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Required Fields Reference */}
            <div className="ca-card p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-list-check text-indigo-600"></i>
                Required Fields
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {MANDATORY_FIELDS.map((field) => (
                  <div
                    key={field}
                    className="flex items-center gap-2 text-sm text-gray-600"
                  >
                    <i className="ri-checkbox-blank-circle-fill text-xs text-indigo-400"></i>
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                      {field}
                    </code>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                <i className="ri-information-line mr-1"></i>
                Arrays: description, experience, primarySkills, locations
              </p>
            </div>
          </div>

          {/* Right - Preview */}
          <div className="space-y-6">
            <div className="ca-card overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <i className="ri-eye-line text-purple-600"></i>
                  Preview
                </h2>
              </div>

              <div className="p-6">
                {parsedJob ? (
                  <div className="space-y-6">
                    {/* Card Preview */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Job Card View
                      </p>
                      <JobCard job={parsedJob} />
                    </div>

                    {/* Details Preview */}
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Full Details
                      </p>
                      <JobPreview job={parsedJob} />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-file-text-line text-3xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 font-medium">No preview yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Paste and validate JSON to see preview
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
